import { pb } from './pocketbase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// S'abonner aux changements d'attendances pour une session spécifique (temps réel)
export const subscribeToSessionAttendances = (sessionId, onAttendanceChange) => {
    return pb.collection('attendances').subscribe('*', async (e) => {
        // Filtrer uniquement les attendances de cette session
        if (e.record && e.record.session_id === sessionId) {
            console.log('📡 Changement d\'attendance détecté:', e.action, e.record.id);
            
            // Récupérer l'attendance complète avec les expansions
            try {
                const fullAttendance = await pb.collection('attendances').getOne(e.record.id, {
                    expand: 'participant_id,session_id'
                });
                
                if (typeof onAttendanceChange === 'function') {
                    onAttendanceChange({
                        action: e.action, // 'create', 'update', 'delete'
                        record: fullAttendance
                    });
                }
            } catch (error) {
                // L'attendance a peut-être été supprimée
                if (e.action === 'delete' && typeof onAttendanceChange === 'function') {
                    onAttendanceChange({
                        action: 'delete',
                        record: e.record
                    });
                }
            }
        }
    });
};

// Se désabonner des changements d'attendances
export const unsubscribeFromAttendances = () => {
    pb.collection('attendances').unsubscribe();
};


// Récupérer les attendances (filtrées automatiquement par les ACL PocketBase)
export const getAttendances = async (options = {}) => {
    try {
        const {
            page = 1,
            perPage = 50,
            filter = null,
            sort = '-created'
        } = options;

        let filterQuery = filter || '';

        const result = await pb.collection('attendances').getList(page, perPage, {
            filter: filterQuery,
            sort: sort,
            expand: 'participant_id,session_id,session_id.teacher_id,session_id.formation_id'
        });

        return {
            items: result.items,
            totalPages: result.totalPages,
            totalItems: result.totalItems,
            page: result.page,
            perPage: result.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des attendances:', error);
        throw error;
    }
};

// Récupérer toutes les attendances d'une session
export const getAttendancesBySessionId = async (sessionId) => {
    try {
        const attendances = await pb.collection('attendances').getFullList({
            filter: `session_id = "${sessionId}"`,
            expand: 'participant_id,session_id'
        });
        return attendances;
    } catch (error) {
        console.error(`Erreur lors de la récupération des attendances pour la session ${sessionId}:`, error);
        throw error;
    }
};

// Récupérer les attendances de l'utilisateur connecté uniquement
export const getAttendancesForCurrentUser = async (options = {}) => {
    try {
        // Vérifier si l'utilisateur est connecté
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Utilisateur non connecté');
        }
        
        const authData = pb.authStore.model;
        
        // Vérifier si l'utilisateur a un student_id
        if (!authData.student_id) {
            throw new Error('Aucun étudiant associé à cet utilisateur');
        }
        
        const {
            page = 1,
            perPage = 50,
            filter = null,
            sort = '-created'
        } = options;

        // Construire le filtre pour cet étudiant spécifique
        let filterQuery = `participant_id = "${authData.student_id}"`;
        
        // Ajouter les filtres supplémentaires si fournis
        if (filter) {
            filterQuery += ` && (${filter})`;
        }

        const result = await pb.collection('attendances').getList(page, perPage, {
            filter: filterQuery,
            sort: sort,
            expand: 'participant_id,session_id,session_id.teacher_id,session_id.formation_id'
        });

        return {
            items: result.items,
            totalPages: result.totalPages,
            totalItems: result.totalItems,
            page: result.page,
            perPage: result.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération des attendances de l\'utilisateur connecté:', error);
        throw error;
    }
};

// Récupérer une attendance spécifique par ID
export const getAttendanceById = async (attendanceId) => {
    try {
        const attendance = await pb.collection('attendances').getOne(attendanceId, {
            expand: 'participant_id,session_id'
        });
        return attendance;
    } catch (error) {
        console.error(`Erreur lors de la récupération de l'attendance ${attendanceId}:`, error);
        throw error;
    }
};

// Créer une nouvelle attendance
export const createAttendance = async (attendanceData) => {
    try {
        const createdAttendance = await pb.collection('attendances').create(attendanceData);
        return createdAttendance;
    } catch (error) {
        console.error('Erreur lors de la création de l\'attendance:', error);
        throw error;
    }
};

// Mettre à jour une attendance
export const updateAttendance = async (attendanceId, attendanceData) => {
    try {
        const updatedAttendance = await pb.collection('attendances').update(attendanceId, attendanceData);
        return updatedAttendance;
    } catch (error) {
        console.error(`Erreur lors de la mise à jour de l'attendance ${attendanceId}:`, error);
        throw error;
    }
};

// Supprimer une attendance
export const deleteAttendance = async (attendanceId) => {
    try {
        await pb.collection('attendances').delete(attendanceId);
        return true;
    } catch (error) {
        console.error(`Erreur lors de la suppression de l'attendance ${attendanceId}:`, error);
        throw error;
    }
};

// Sauvegarder une signature de présence basée sur l'attendance
export const saveAttendanceSignature = async (attendanceId, signatureFile, signerRole = 'student') => {
    if (!attendanceId || !signatureFile) {
        throw new Error('ID d\'attendance et fichier de signature requis');
    }

    try {

        const authData = JSON.parse(localStorage.getItem('pocketbase_auth'));
        if (!authData || !authData.token) {
            throw new Error('Non authentifié');
        }
        
        const formData = new FormData();
        formData.append('signatureFile', signatureFile);
        formData.append('signerRole', signerRole);
        
        // Ajouter les données de géolocalisation si disponibles
        if (navigator.geolocation) {
            try {
                const position = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    });
                });
                
                formData.append('longitude', position.coords.longitude.toString());
                formData.append('latitude', position.coords.latitude.toString());
                console.log('Géolocalisation ajoutée:', {
                    longitude: position.coords.longitude,
                    latitude: position.coords.latitude
                });
            } catch (geoError) {
                console.warn('Impossible de récupérer la géolocalisation:', geoError);
                // Continuer sans géolocalisation
            }
        }

        const response = await fetch(`${API_BASE_URL}/api/attendances/${attendanceId}/save-signature`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authData.token}`
            },
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erreur lors de la sauvegarde de la signature');
        }

        return await response.json();

    } catch (error) {
        console.error("Erreur lors de la sauvegarde de la signature:", error);
        throw new Error(`Erreur lors de l'enregistrement: ${error.message}`);
    }
};

export const generateDocuments = async (sessionId) => {
    try {
        // Récupérer le token d'authentification
        const authData = JSON.parse(localStorage.getItem('pocketbase_auth'));
        if (!authData || !authData.token) {
            throw new Error('Non authentifié');
        }

        // Faire la requête avec responseType blob pour récupérer directement le PDF
        const response = await fetch(`${API_BASE_URL}/api/attendances/generate-documents`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authData.token}`
            },
            body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Erreur lors de la génération des documents');
        }

        // Récupérer le blob du PDF
        const blob = await response.blob();

        // Créer un nom de fichier avec la date actuelle
        const fileName = `feuille_emargement_${sessionId}_${new Date().toISOString().split('T')[0]}.pdf`;

        // Créer un URL pour le blob
        const url = window.URL.createObjectURL(blob);

        // Créer un lien invisible pour déclencher le téléchargement
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = fileName;

        // Ajouter le lien au DOM et cliquer dessus
        document.body.appendChild(a);
        a.click();

        // Nettoyer
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true, fileName };
    } catch (error) {
        console.error('Erreur lors de la génération des documents:', error);
        throw error;
    }
};

// Exporter le rapport d'absences global en Excel
export const exportAbsencesExcel = async (partnerId, startDate, endDate) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/attendances/export-absences-excel`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': pb.authStore.token
            },
            body: JSON.stringify({
                partnerId,
                startDate,
                endDate
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Erreur lors de la génération du rapport Excel');
        }

        // Récupérer le fichier Excel
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Créer un lien pour télécharger le fichier
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `rapport-absences-${startDate}-${endDate}.xlsx`;

        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
    } catch (error) {
        console.error('Erreur lors de l\'export du rapport d\'absences Excel:', error);
        throw error;
    }
};

// Exporter le rapport d'absences global en PDF
export const exportAbsencesReport = async (partnerId, startDate, endDate) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/attendances/export-absences-report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': pb.authStore.token
            },
            body: JSON.stringify({
                partnerId,
                startDate,
                endDate
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Erreur lors de la génération du rapport');
        }

        // Récupérer le PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Créer un lien pour télécharger le fichier
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `rapport-absences-${startDate}-${endDate}.pdf`;

        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
    } catch (error) {
        console.error('Erreur lors de l\'export du rapport d\'absences:', error);
        throw error;
    }
};

// Exporter le rapport de présences d'un élève en PDF
export const exportStudentAttendanceReport = async (studentId, startDate, endDate) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/attendances/export-student-report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': pb.authStore.token
            },
            body: JSON.stringify({
                studentId,
                startDate,
                endDate
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Erreur lors de la génération du rapport');
        }

        // Récupérer le PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Créer un lien pour télécharger le fichier
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `rapport-presences-${studentId}.pdf`;

        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return { success: true };
    } catch (error) {
        console.error('Erreur lors de l\'export du rapport de présences:', error);
        throw error;
    }
};
